import { useEffect, useState } from "react";
import type { UserDTO } from "../../shared/types";
import { TextLink } from "../components/TextLink";
import { Button, ButtonLink } from "../components/ui";
import { ApiError, get, post } from "../lib/api";
import { navigate } from "../lib/router";

type Providers = { auth0: boolean; adminToken: boolean; allowedEmailDomains: string[] };

export function LoginPage({ onLogin }: { onLogin: (user: UserDTO) => void }) {
  const params = new URLSearchParams(location.search);
  const next = params.get("next") || "/";
  const [providers, setProviders] = useState<Providers | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(params.get("error"));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    get<Providers>("/api/auth/providers").then(
      (p) => {
        setProviders(p);
        if (!p.auth0) setShowToken(true);
      },
      () => setShowToken(true),
    );
  }, []);

  const domains = providers?.allowedEmailDomains.map((d) => `@${d}`).join(", ") ?? "";

  return (
    <div className="flex h-full items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-2xl border border-line bg-surface p-6 shadow-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src="/favicon.png" alt="" className="size-12 rounded-xl object-cover" />
          <h1 className="mt-3 text-heading font-semibold">AgentOS</h1>
          <p className="mt-1 text-sm text-muted">Sign in to continue</p>
        </div>

        {error && <p className="mb-4 rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">{error}</p>}

        {providers?.auth0 && (
          <>
            <ButtonLink variant="primary" href={`/api/auth/auth0/login?next=${encodeURIComponent(next)}`} className="w-full gap-2 py-2.5">
              Continue with your work account
            </ButtonLink>
            <p className="mt-2 text-center text-xs text-muted">Only {domains} accounts can sign in.</p>
          </>
        )}

        {providers?.auth0 && !showToken && (
          <TextLink onClick={() => setShowToken(true)} className="mt-6 w-full text-center text-xs text-faint">
            Use a token
          </TextLink>
        )}

        {showToken && (
          <form
            className={providers?.auth0 ? "mt-6 border-t border-line pt-5" : ""}
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError(null);
              try {
                const { user } = await post<{ user: UserDTO }>("/api/login", { token });
                onLogin(user);
                navigate(next, true);
              } catch (err) {
                setError(err instanceof ApiError ? err.message : "Sign-in failed");
              } finally {
                setBusy(false);
              }
            }}
          >
            <label className="mb-1 block text-xs font-medium text-muted">Token</label>
            <input
              type="password"
              autoFocus={!providers?.auth0}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="A token of yours, or the admin token"
              className="mb-3 w-full rounded-md border border-line bg-transparent px-3 py-2 text-sm outline-none focus:border-muted"
            />
            <Button disabled={busy || !token} className="w-full">
              {busy ? "Signing in…" : "Sign in with token"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
